import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Gift, LogOut } from "lucide-react";
import { api, useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

const statusStyles = {
  confirmed: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  awaiting_deposit: "text-zinc-300 border-zinc-600 bg-zinc-800/50",
  completed: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [giftCards, setGiftCards] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/login?next=/account");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings/mine").then((r) => setBookings(r.data.bookings)).catch(() => setBookings([]));
    api.get("/gift-cards/mine").then((r) => setGiftCards(r.data.gift_cards)).catch(() => setGiftCards([]));
  }, [user]);

  if (!user) return null;

  return (
    <main className="min-h-screen pt-28 pb-24 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-testid="account-page">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-amber-400/90 font-mono-gold mb-3">Members Concierge</p>
          <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl">
            Hello, <span className="gold-text">{user.name.split(" ")[0]}</span>
          </h1>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="account-logout-button"
          className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-300 border border-zinc-800 hover:border-amber-500/40 px-5 py-2.5 rounded-full transition-colors"
        >
          <LogOut size={13} /> Sign Out
        </button>
      </Reveal>

      <Reveal delay={0.1} className="mt-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-semibold uppercase tracking-tight text-xl flex items-center gap-3">
            <CalendarDays size={18} className="text-amber-400" /> My Bookings
          </h2>
          <Link to="/book" data-testid="account-new-booking-button" className="gold-foil text-black text-xs font-semibold uppercase tracking-[0.2em] px-5 py-2.5 rounded-full">
            New Booking
          </Link>
        </div>
        {bookings === null ? (
          <p className="text-zinc-500 text-sm">Loading…</p>
        ) : bookings.length === 0 ? (
          <div className="gold-border-card p-8 text-center text-zinc-500 text-sm" data-testid="bookings-empty">
            No bookings yet — your next showroom shine is one click away.
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
              <div key={b.id} className="gold-border-card p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`booking-item-${b.id}`}>
                <div>
                  <p className="font-display font-semibold uppercase tracking-tight">{b.package_name}</p>
                  <p className="text-zinc-500 text-xs mt-1">{b.vehicle_year} {b.vehicle_make} {b.vehicle_model} · {b.address}</p>
                  <p className="text-zinc-400 text-xs mt-1 font-mono-gold tracking-widest">{b.date} · {b.time_slot}</p>
                </div>
                <div className="text-right">
                  <span className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1.5 rounded-full ${statusStyles[b.status] || statusStyles.confirmed}`} data-testid={`booking-status-${b.id}`}>
                    {b.status === "awaiting_deposit" ? "Awaiting Deposit" : b.status}
                  </span>
                  <p className="gold-text font-display font-bold text-lg mt-2">{b.price_label || `$${b.price}`}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Reveal>

      <Reveal delay={0.15} className="mt-14">
        <h2 className="font-display font-semibold uppercase tracking-tight text-xl flex items-center gap-3 mb-6">
          <Gift size={18} className="text-amber-400" /> My Gift Cards
        </h2>
        {giftCards === null ? (
          <p className="text-zinc-500 text-sm">Loading…</p>
        ) : giftCards.length === 0 ? (
          <div className="gold-border-card p-8 text-center text-zinc-500 text-sm" data-testid="giftcards-empty">
            No gift cards purchased yet. <Link to="/gift-cards" className="text-amber-300 underline underline-offset-4">Send one</Link>.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="giftcards-list">
            {giftCards.map((g) => (
              <div key={g.id} className="gold-border-card p-6" data-testid={`giftcard-item-${g.id}`}>
                <div className="flex justify-between items-start">
                  <p className="gold-text font-display font-extrabold text-3xl">${g.amount}</p>
                  <span className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1.5 rounded-full ${g.status === "active" ? statusStyles.confirmed : statusStyles.awaiting_deposit}`}>
                    {g.status === "active" ? "Delivered" : "Pending"}
                  </span>
                </div>
                <p className="text-zinc-400 text-xs mt-3">To: {g.recipient_name} ({g.recipient_email})</p>
                {g.code && <p className="font-mono-gold text-amber-400/90 text-sm tracking-[0.25em] mt-3" data-testid={`giftcard-code-${g.id}`}>{g.code}</p>}
              </div>
            ))}
          </div>
        )}
      </Reveal>
    </main>
  );
}
